import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const CalendarWidget = ({ language, direction }) => {
  const navigate = useNavigate();
  const [selectedDate] = useState(new Date());

  const todayEvents = [
    {
      id: 1,
      titleEn: 'Court Hearing - Case #2024-156',
      titleFr: 'Audience - Dossier #2024-156',
      titleAr: 'جلسة المحكمة - القضية #2024-156',
      time: '10:00',
      type: 'hearing',
      location: 'Tribunal de Première Instance',
      clientName: 'Mohammed Alami'
    },
    {
      id: 2,
      titleEn: 'Client Meeting - Sarah Bennani',
      titleFr: 'Réunion Client - Sarah Bennani',
      titleAr: 'اجتماع العميل - سارة بناني',
      time: '14:30',
      type: 'meeting',
      location: 'Office',
      clientName: 'Sarah Bennani'
    },
    {
      id: 3,
      titleEn: 'Document Review - Contract',
      titleFr: 'Révision Document - Contrat',
      titleAr: 'مراجعة المستند - العقد',
      time: '16:00',
      type: 'task',
      location: 'Office',
      clientName: 'Karim Idrissi'
    }
  ];

  const upcomingEvents = [
    {
      id: 4,
      titleEn: 'Mediation Session',
      titleFr: 'Séance de Médiation',
      titleAr: 'جلسة الوساطة',
      date: '2026-01-28',
      time: '11:00',
      type: 'mediation'
    },
    {
      id: 5,
      titleEn: 'Court Hearing - Case #2024-189',
      titleFr: 'Audience - Dossier #2024-189',
      titleAr: 'جلسة المحكمة - القضية #2024-189',
      date: '2026-01-29',
      time: '09:30',
      type: 'hearing'
    }
  ];

  const getTitle = (event) => {
    if (language === 'fr') return event?.titleFr;
    if (language === 'ar') return event?.titleAr;
    return event?.titleEn;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    if (language === 'ar') {
      return new Intl.DateTimeFormat('ar-MA', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })?.format(d);
    }
    return new Intl.DateTimeFormat('fr-MA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })?.format(d);
  };

  const todayLabel = {
    en: "Today\'s Schedule",
    fr: "Programme d\'aujourd\'hui",
    ar: 'جدول اليوم'
  };

  const upcomingLabel = {
    en: 'Upcoming Events',
    fr: 'Événements à venir',
    ar: 'الأحداث القادمة'
  };

  const viewAllLabel = {
    en: 'View Full Calendar',
    fr: 'Voir le calendrier complet',
    ar: 'عرض التقويم الكامل'
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'hearing':
        return 'Scale';
      case 'meeting':
        return 'Users';
      case 'task':
        return 'FileText';
      case 'mediation':
        return 'MessageSquare';
      default:
        return 'Calendar';
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'hearing':
        return 'bg-error/10 text-error';
      case 'meeting':
        return 'bg-primary/10 text-primary';
      case 'task':
        return 'bg-warning/10 text-warning';
      case 'mediation':
        return 'bg-success/10 text-success';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-sm">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            {todayLabel?.[language]}
          </h2>
          <button
            onClick={() => navigate('/calendar-scheduling')}
            className="text-sm text-primary hover:text-primary/80 transition-colors"
          >
            {viewAllLabel?.[language]}
          </button>
        </div>
        <p className="text-sm text-muted-foreground">{formatDate(selectedDate)}</p>
      </div>
      <div className="p-4 md:p-6 space-y-3">
        {todayEvents?.length > 0 ? (
          todayEvents?.map((event) => (
            <div
              key={event?.id}
              className="flex gap-3 p-3 rounded-lg hover:bg-muted transition-colors cursor-pointer"
              onClick={() => navigate('/calendar-scheduling')}
            >
              <div className="flex-shrink-0">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${getEventColor(
                    event?.type
                  )}`}
                >
                  <Icon name={getEventIcon(event?.type)} size={18} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {getTitle(event)}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Icon name="Clock" size={14} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {event?.time}
                  </span>
                  {event?.location && (
                    <>
                      <span className="text-muted-foreground">•</span>
                      <Icon
                        name="MapPin"
                        size={14}
                        className="text-muted-foreground"
                      />
                      <span className="text-xs text-muted-foreground truncate">
                        {event?.location}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon
              name="Calendar"
              size={48}
              className="text-muted-foreground mx-auto mb-2"
            />
            <p className="text-sm text-muted-foreground">
              {language === 'fr' && 'Aucun événement prévu'}
              {language === 'ar' && 'لا توجد أحداث مجدولة'}
              {language === 'en' && 'No events scheduled'}
            </p>
          </div>
        )}
      </div>
      {upcomingEvents?.length > 0 && (
        <div className="p-4 md:p-6 border-t border-border">
          <h3 className="text-sm font-semibold text-foreground mb-3">
            {upcomingLabel?.[language]}
          </h3>
          <div className="space-y-2">
            {upcomingEvents?.map((event) => (
              <div
                key={event?.id}
                className="flex items-center justify-between p-2 rounded hover:bg-muted transition-colors cursor-pointer"
                onClick={() => navigate('/calendar-scheduling')}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      event?.type === 'hearing' ? 'bg-error' : 'bg-primary'
                    }`}
                  />
                  <span className="text-sm text-foreground truncate">
                    {getTitle(event)}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                  {event?.time}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarWidget;